import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { HlmPaginationImports } from '@spartan-ng/helm/pagination';
import { PAGE_ELLIPSIS, paginationRange, type PageItem } from '../../utils/pagination.utils';

/**
 * Pagination bar shared by the dashboard tables.
 *
 * Purely event driven — the tables page in memory or through the API, the page
 * number never lives in the URL, so the Helm links are used without a route.
 */
@Component({
  selector: 'app-table-pagination',
  standalone: true,
  imports: [HlmPaginationImports],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <nav hlmPagination aria-label="Seitennavigation">
      <ul hlmPaginationContent>
        <li hlmPaginationItem>
          <hlm-pagination-previous
            text="Zurück"
            aria-label="Vorherige Seite"
            [class.pointer-events-none]="page() <= 1"
            [class.opacity-50]="page() <= 1"
            (click)="go(page() - 1)" />
        </li>

        @for (item of items(); track $index) {
          <li hlmPaginationItem>
            @if (item === ellipsis) {
              <hlm-pagination-ellipsis srOnlyText="Weitere Seiten" />
            } @else {
              <a
                hlmPaginationLink
                [isActive]="item === page()"
                [attr.aria-label]="'Seite ' + item"
                (click)="go(+item)">
                {{ item }}
              </a>
            }
          </li>
        }

        <li hlmPaginationItem>
          <hlm-pagination-next
            text="Weiter"
            aria-label="Nächste Seite"
            [class.pointer-events-none]="page() >= totalPages()"
            [class.opacity-50]="page() >= totalPages()"
            (click)="go(page() + 1)" />
        </li>
      </ul>
    </nav>
  `,
})
export class TablePaginationComponent {
  readonly page = input.required<number>();
  readonly totalPages = input.required<number>();
  readonly pageChange = output<number>();

  protected readonly ellipsis = PAGE_ELLIPSIS;
  protected readonly items = computed<PageItem[]>(() =>
    paginationRange(this.page(), this.totalPages()),
  );

  protected go(page: number) {
    if (page >= 1 && page <= this.totalPages() && page !== this.page()) {
      this.pageChange.emit(page);
    }
  }
}
